// Sentry seam (specs/observability). Deliberately a no-op until
// VITE_SENTRY_DSN is set at build time: the dynamic import below means the
// Sentry chunk is never even fetched without a DSN, so unconfigured builds are
// byte-identical in behavior to pre-Sentry ones. Replay samples 0% of normal
// sessions and 100% of error sessions — "watch what the user did" without
// recording everyone.
import { getDiagnosticsCode } from './telemetry';

/**
 * Envelopes go through our Convex deployment (…convex.site/sentry-tunnel),
 * not straight to *.ingest.sentry.io — ad blockers block the latter for a
 * meaningful slice of real users. Derived from the Convex URL so dev/prod
 * follow automatically; if it can't be derived, fall back to direct ingest.
 */
function tunnelUrl(): string | undefined {
  const convexUrl = import.meta.env.PUBLIC_CONVEX_URL ?? import.meta.env.VITE_CONVEX_URL;
  if (!convexUrl || !convexUrl.includes('.convex.cloud')) return undefined;
  return `${convexUrl.replace('.convex.cloud', '.convex.site')}/sentry-tunnel`;
}

export function initSentry(): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn) return;
  void import('@sentry/svelte')
    .then(Sentry => {
      Sentry.init({
        dsn,
        tunnel: tunnelUrl(),
        environment: import.meta.env.MODE,
        integrations: [
          // Replays are useless fully masked (asterisk soup, no visible
          // motion), and this app renders no sensitive text outside inputs.
          // Page text + media show; maskAllInputs stays on (default), so
          // anything typed — names, emails, Clerk fields — is still hidden.
          // Stripe checkout happens on stripe.com and is never recorded.
          Sentry.replayIntegration({ maskAllText: false, blockAllMedia: false })
        ],
        replaysSessionSampleRate: 0,
        replaysOnErrorSampleRate: 1.0
      });
      // The on-screen diagnostics code doubles as the join key between a
      // user's verbal report, the Convex clientDiagnostics batch, and the
      // Sentry event/replay.
      Sentry.setTag('diagnostics_code', getDiagnosticsCode());
    })
    .catch(() => {
      /* observability must never break the app */
    });
}
